import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Image,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const FormInputField = ({ label, name, type = "text", rows, accept, value, onChange, error }) => (
    <Form.Group className="mb-3">
        <Form.Label className="fw-semibold small text-muted text-uppercase">
            {label}
        </Form.Label>
        <Form.Control
            type={type !== "textarea" ? type : undefined}
            as={type === "textarea" ? "textarea" : undefined}
            rows={rows}
            accept={accept}
            name={name}
            value={type !== "file" ? value : undefined}
            onChange={onChange}
            isInvalid={!!error}
            className="bg-light border-0 py-2 px-3"
        />
        <Form.Control.Feedback type="invalid">
            {Array.isArray(error) ? error[0] : error}
        </Form.Control.Feedback>
    </Form.Group>
);

const AboutUsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        hero_title: "",
        hero_subtitle: "",
        hero_description: "",
        hero_image: null,

        story_title: "",
        story_content: "",
        story_image: null,

        mission_title: "",
        mission_content: "",
        mission_image: null,

        quality_section_title: "",
        quality_section_content: "",

        quality_item_one_icon: null,
        quality_item_one_title: "",
        quality_item_one_description: "",

        quality_item_two_icon: null,
        quality_item_two_title: "",
        quality_item_two_description: "",

        quality_item_three_icon: null,
        quality_item_three_title: "",
        quality_item_three_description: "",
    });

    const [previews, setPreviews] = useState({
        hero_image: null,
        story_image: null,
        mission_image: null,
        quality_item_one_icon: null,
        quality_item_two_icon: null,
        quality_item_three_icon: null,
    });

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    const getAboutUsData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/about-us`,
                { headers: getAuthHeaders() }
            );

            const data = response.data?.data;

            if (data) {
                setForm({
                    hero_title: data.hero_title || "",
                    hero_subtitle: data.hero_subtitle || "",
                    hero_description: data.hero_description || "",
                    hero_image: null,

                    story_title: data.story_title || "",
                    story_content: data.story_content || "",
                    story_image: null,

                    mission_title: data.mission_title || "",
                    mission_content: data.mission_content || "",
                    mission_image: null,

                    quality_section_title: data.quality_section_title || "",
                    quality_section_content: data.quality_section_content || "",

                    quality_item_one_icon: null,
                    quality_item_one_title: data.quality_item_one_title || "",
                    quality_item_one_description: data.quality_item_one_description || "",

                    quality_item_two_icon: null,
                    quality_item_two_title: data.quality_item_two_title || "",
                    quality_item_two_description: data.quality_item_two_description || "",

                    quality_item_three_icon: null,
                    quality_item_three_title: data.quality_item_three_title || "",
                    quality_item_three_description: data.quality_item_three_description || "",
                });

                setPreviews({
                    hero_image: data.hero_image ? getImageUrl(data.hero_image) : null,
                    story_image: data.story_image ? getImageUrl(data.story_image) : null,
                    mission_image: data.mission_image ? getImageUrl(data.mission_image) : null,
                    quality_item_one_icon: data.quality_item_one_icon ? getImageUrl(data.quality_item_one_icon) : null,
                    quality_item_two_icon: data.quality_item_two_icon ? getImageUrl(data.quality_item_two_icon) : null,
                    quality_item_three_icon: data.quality_item_three_icon ? getImageUrl(data.quality_item_three_icon) : null,
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load About Us details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAboutUsData();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === "file") {
            const file = files?.[0] || null;
            setForm((prev) => ({ ...prev, [name]: file }));

            if (file) {
                setPreviews((prev) => ({
                    ...prev,
                    [name]: URL.createObjectURL(file),
                }));
            }

            setErrors((prev) => ({ ...prev, [name]: "" }));
            return;
        }

        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            formData.append("_method", "PUT");

            Object.keys(form).forEach((key) => {
                if (form[key] !== null && form[key] !== undefined) {
                    formData.append(key, form[key]);
                }
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/about-us/1`,
                formData,
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || "About Us updated successfully.");
            await getAboutUsData();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Please fix validation errors.");
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Helper function to render feature items dynamically
    const renderFeatureItem = (itemKey, itemTitle) => (
        <>
            <h6 className="fw-bold mb-3">{itemTitle}</h6>
            <Row className="g-3 mb-4">
                <Col md={4}>
                    <FormInputField
                        label="Icon Image"
                        name={`${itemKey}_icon`}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.svg"
                        onChange={handleChange}
                        error={errors[`${itemKey}_icon`]}
                    />
                    {previews[`${itemKey}_icon`] && (
                        <Image
                            src={previews[`${itemKey}_icon`]}
                            rounded
                            className="mt-2"
                            style={{ height: "40px", width: "40px", objectFit: "contain" }}
                        />
                    )}
                </Col>
                <Col md={8}>
                    <FormInputField
                        label="Title"
                        name={`${itemKey}_title`}
                        value={form[`${itemKey}_title`]}
                        onChange={handleChange}
                        error={errors[`${itemKey}_title`]}
                    />
                </Col>
                <Col md={12}>
                    <FormInputField
                        label="Description"
                        name={`${itemKey}_description`}
                        type="textarea"
                        rows={2}
                        value={form[`${itemKey}_description`]}
                        onChange={handleChange}
                        error={errors[`${itemKey}_description`]}
                    />
                </Col>
            </Row>
        </>
    );

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">About Us Settings</h2>
                        <p className="text-muted mb-0">
                            Manage Hero, Story, Mission, and Quality Feature sections.
                        </p>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <div className="mt-3 text-muted">Loading settings...</div>
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        {/* HERO SECTION */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Hero Section
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Hero Title"
                                            name="hero_title"
                                            value={form.hero_title}
                                            onChange={handleChange}
                                            error={errors.hero_title}
                                        />
                                        <FormInputField
                                            label="Hero Subtitle"
                                            name="hero_subtitle"
                                            value={form.hero_subtitle}
                                            onChange={handleChange}
                                            error={errors.hero_subtitle}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <FormInputField
                                            label="Hero Image"
                                            name="hero_image"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp,.svg"
                                            onChange={handleChange}
                                            error={errors.hero_image}
                                        />
                                        {previews.hero_image && (
                                            <Image
                                                src={previews.hero_image}
                                                rounded
                                                style={{ height: "100px", objectFit: "cover" }}
                                            />
                                        )}
                                    </Col>

                                    <Col md={12}>
                                        <FormInputField
                                            label="Hero Description"
                                            name="hero_description"
                                            type="textarea"
                                            rows={3}
                                            value={form.hero_description}
                                            onChange={handleChange}
                                            error={errors.hero_description}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* STORY SECTION */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Our Story Section
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Story Title"
                                            name="story_title"
                                            value={form.story_title}
                                            onChange={handleChange}
                                            error={errors.story_title}
                                        />
                                        <FormInputField
                                            label="Story Image"
                                            name="story_image"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp,.svg"
                                            onChange={handleChange}
                                            error={errors.story_image}
                                        />
                                        {previews.story_image && (
                                            <Image
                                                src={previews.story_image}
                                                rounded
                                                className="mt-2"
                                                style={{ height: "90px", objectFit: "cover" }}
                                            />
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <FormInputField
                                            label="Story Content"
                                            name="story_content"
                                            type="textarea"
                                            rows={5}
                                            value={form.story_content}
                                            onChange={handleChange}
                                            error={errors.story_content}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* MISSION SECTION */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Our Mission Section
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Mission Title"
                                            name="mission_title"
                                            value={form.mission_title}
                                            onChange={handleChange}
                                            error={errors.mission_title}
                                        />
                                        <FormInputField
                                            label="Mission Image"
                                            name="mission_image"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp,.svg"
                                            onChange={handleChange}
                                            error={errors.mission_image}
                                        />
                                        {previews.mission_image && (
                                            <Image
                                                src={previews.mission_image}
                                                rounded
                                                className="mt-2"
                                                style={{ height: "90px", objectFit: "cover" }}
                                            />
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <FormInputField
                                            label="Mission Content"
                                            name="mission_content"
                                            type="textarea"
                                            rows={5}
                                            value={form.mission_content}
                                            onChange={handleChange}
                                            error={errors.mission_content}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* QUALITY SECTION */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Quality & Features Section
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Section Title"
                                            name="quality_section_title"
                                            value={form.quality_section_title}
                                            onChange={handleChange}
                                            error={errors.quality_section_title}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <FormInputField
                                            label="Section Content"
                                            name="quality_section_content"
                                            type="textarea"
                                            rows={2}
                                            value={form.quality_section_content}
                                            onChange={handleChange}
                                            error={errors.quality_section_content}
                                        />
                                    </Col>
                                </Row>

                                <hr className="my-4" />

                                {renderFeatureItem("quality_item_one", "Feature Item 1")}
                                {renderFeatureItem("quality_item_two", "Feature Item 2")}
                                {renderFeatureItem("quality_item_three", "Feature Item 3")}
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end mb-5">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                                className="px-5 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save About Us"
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Container>
        </PageLayout>
    );
};

export default AboutUsPage;